/**
 * Play-along teaching tracks recovered from the legacy WP Site (the
 * iWeb-era "Styles & Videos" subtree at
 * `wp-site-backup/files/public_html/Styles_Videos_files/`). 19 audio
 * tracks plus accompanying notation images.
 *
 * Categories:
 *   - "Easy" bass lines (Blues / Classic Rock / R&B / Reggae) at two
 *     tempos each — beginner-friendly play-along loops.
 *   - "Get Your Groove On 2 & 4" — straight vs. swing eighths variants
 *     for working on subdivision feel.
 *
 * Audio files live at /audio/play-along-tracks/<filename> and notation
 * images at /images/play-along-tracks/<filename> after running
 * `npx tsx scripts/copy-play-along-assets.mts` one-time from the WP
 * backup. The route is gated behind `betaPreview` so the client can
 * review the content on beta before deciding whether to ship it.
 */

export interface PlayAlongTrack {
  /** Display label for this audio (e.g. "Slow", "At tempo"). */
  readonly tempo: string;
  /** Destination filename under public/audio/play-along-tracks/. */
  readonly filename: string;
  /** Source filename in WP backup Styles_Videos_files/. */
  readonly sourceFilename: string;
}

export interface PlayAlongNotation {
  /** Destination filename under public/images/play-along-tracks/. */
  readonly filename: string;
  /** Source filename in WP backup Styles_Videos_files/. */
  readonly sourceFilename: string;
  /** alt text describing the notation image. */
  readonly alt: string;
  /** aspectRatio string for CSS aspect-ratio (after webp conversion). */
  readonly aspectRatio: string;
}

export interface PlayAlongGroup {
  /** Slug for in-page anchor + group key. */
  readonly slug: string;
  /** Display heading for the group. */
  readonly title: string;
  /** Optional short description shown under the group heading. */
  readonly description?: string;
  /** Tracks in display order. */
  readonly tracks: readonly PlayAlongTrack[];
  /** Optional notation image for the group. */
  readonly notation?: PlayAlongNotation;
}

export const PLAY_ALONG_TRACKS: readonly PlayAlongGroup[] = [
  {
    slug: 'easy-blues',
    title: 'Easy Blues',
    description: 'Beginner-friendly 12-bar blues bass line.',
    tracks: [
      {
        tempo: 'Slow',
        filename: 'easy-blues-slow.mp3',
        sourceFilename: 'Easy Blues bass Line-Slow Tempo.mp3',
      },
      {
        tempo: 'At tempo',
        filename: 'easy-blues-at-tempo.mp3',
        sourceFilename: 'Easy Blues bass Line-At Tempo.mp3',
      },
    ],
    notation: {
      filename: 'easy-blues-line.webp',
      sourceFilename: 'Easy Blues Bass Line.png',
      alt: 'Notation for the Easy Blues bass line',
      aspectRatio: '577 / 544',
    },
  },
  {
    slug: 'easy-classic-rock',
    title: 'Easy Classic Rock',
    description: 'Straight-eighths classic-rock bass line.',
    tracks: [
      {
        tempo: 'Slow',
        filename: 'easy-classic-rock-slow.mp3',
        sourceFilename: 'Easy Classic Rock Bass Line-Slow tempo.mp3',
      },
      {
        tempo: 'At tempo',
        filename: 'easy-classic-rock-at-tempo.mp3',
        sourceFilename: 'Easy Classic Rock Bass Line-At Tempo.mp3',
      },
    ],
    notation: {
      filename: 'easy-classic-rock-line.webp',
      sourceFilename: 'Easy Classic Rock Line.png',
      alt: 'Notation for the Easy Classic Rock bass line',
      aspectRatio: '571 / 622',
    },
  },
  {
    slug: 'easy-rnb',
    title: 'Easy R&B',
    description: 'Soul/R&B bass line groove.',
    tracks: [
      {
        tempo: 'Slow',
        filename: 'easy-rnb-slow.mp3',
        sourceFilename: 'Easy R&B Bass Line-Slow Tempo Track.mp3',
      },
      {
        tempo: 'Medium',
        filename: 'easy-rnb-medium.mp3',
        sourceFilename: 'Easy R&B Bass Line-Medium Tempo Track.mp3',
      },
    ],
    notation: {
      filename: 'easy-rnb-line.webp',
      sourceFilename: 'Easy R&B Line.png',
      alt: 'Notation for the Easy R&B bass line',
      aspectRatio: '577 / 600',
    },
  },
  {
    slug: 'easy-reggae',
    title: 'Easy Reggae',
    description: 'Reggae bass line emphasizing the one-drop feel.',
    tracks: [
      {
        tempo: 'Slow',
        filename: 'easy-reggae-slow.mp3',
        sourceFilename: 'Easy Reggae Bass Line-Slow Tempo.mp3',
      },
      {
        tempo: 'At tempo',
        filename: 'easy-reggae-at-tempo.mp3',
        sourceFilename: 'Easy Reggae Bass Line-At Tempo 1.mp3',
      },
    ],
    notation: {
      filename: 'easy-reggae-line.webp',
      sourceFilename: 'Easy Reggae Line.png',
      alt: 'Notation for the Easy Reggae bass line',
      aspectRatio: '569 / 618',
    },
  },
  {
    slug: 'get-your-groove-on',
    title: 'Get Your Groove On — 2 & 4',
    description:
      'Subdivision-focused groove track. Two variants: straight eighths vs. swing eighths.',
    tracks: [
      {
        tempo: 'Straight 8ths',
        filename: 'get-your-groove-on-2-and-4-straight-8ths.mp3',
        sourceFilename: 'Get Your Groove On-2&4-St 8ths.mp3',
      },
      {
        tempo: 'Swing 8ths',
        filename: 'get-your-groove-on-2-and-4-swing-8ths.mp3',
        sourceFilename: 'Get Your Groove On-2&4-Swing 8ths.mp3',
      },
    ],
    notation: {
      filename: 'get-your-groove-notation.webp',
      sourceFilename: 'Get Your Groove-Notation.png',
      alt: 'Notation for the Get Your Groove On 2 & 4 exercise',
      aspectRatio: '584 / 572',
    },
  },
];
